import { pick } from 'lodash';
import { IS_ONE_SEALINK } from 'utils/constant';
import {
  fireEvent,
  getBooking,
  getCheckoutForm,
  inStorage,
  onOverviewOrCheckout,
  prefillEmail,
  removeOutDated,
  storeModalState,
} from './helpers';

const EVENT_NAME = 'abandonedCart';

const getPassenger = (id, types) => {
  const type = types.find((item) => item.id === id) || {};
  return pick(type, 'passenger_type_id');
};

const getPassengerTypes = (ids, types) => {
  const passengerTypes = {};

  ids.forEach((id) => {
    const type = types.find((item) => item.id === id) || {};
    passengerTypes[type.passenger_type_id] = passengerTypes[
      type.passenger_type_id
    ]
      ? ++passengerTypes[type.passenger_type_id]
      : 1;
  });

  return passengerTypes;
};

const getVehicleType = (id, types) => {
  const type = types.find((item) => item.id === id) || {};

  return {
    length: type.length,
    width: type.width,
    height: type.height,
    weight: type.weight,
    details: type.details,
    registration: type.registration,
    vehicle_type_id: type.vehicle_type_id,
    make_code: type.make_code,
    family_code: type.family_code,
    body_style_code: type.body_style_code,
    year_code: type.year_code,
    variant_code: type.variant_code,
    attachments: type.attachments.map((attachment) => ({
      code: attachment.code,
      description: attachment.description,
      length_value: attachment.length_value,
      height_value: attachment.height_value,
      width_value: attachment.width_value,
      weight_value: attachment.weight_value,
      size_unit: attachment.size_unit,
      weight_unit: attachment.weight_unit,
    })),
  };
};

const hasUnpaidBooking = () => {
  const booking = getBooking();
  const checkoutForm = getCheckoutForm();
  const { email } = checkoutForm;
  const { balance_in_cents: balanceInCents, customer_contact_email } = booking;
  prefillEmail(email || customer_contact_email);
  return balanceInCents !== undefined && balanceInCents > 0;
};

const buildPayload = (emailFromForm) => {
  let result = {};
  const rebuildData = {
    reservations: [],
    customer: {},
  };
  const booking = getBooking();
  const checkoutForm = getCheckoutForm();
  const { firstName, lastName, userPhoneNumber, countryId, postCode } =
    checkoutForm;
  let userName = null;
  if (firstName && lastName) {
    userName = `${firstName} ${lastName}`;
  }
  const {
    balance_in_cents: balanceInCents,
    reference,
    reservations_attributes: reservations,
    passengers_attributes: passengerTypes,
    vehicles_attributes: vehiclesTypes,
    customer_contact_name,
    customer_contact_phone,
    customer_contact_mobile,
    country_id,
    post_code,
  } = booking;

  // Booking is not paid yet.
  if (balanceInCents > 0) {
    result = {
      booking: {
        booking_reference: reference,
        site: ecom.craftConfig.site,
        balance_in_cents: balanceInCents,
        resources: reservations.map(
          ({
            resource_id,
            gross_including_packaged_item_in_cents,
            first_travel_date,
            last_travel_date,
            passenger_ids,
          }) => ({
            resourceId: resource_id,
            price: gross_including_packaged_item_in_cents,
            travel_start_date: first_travel_date._value,
            travel_end_date: last_travel_date._value,
            passengers: passenger_ids.map((paxId) =>
              getPassenger(paxId, passengerTypes)
            ),
          })
        ),
      },
      customer: {
        customer_contact_name: userName || customer_contact_name,
        customer_contact_phone,
        customer_contact_mobile: userPhoneNumber || customer_contact_mobile,
        customer_contact_email: emailFromForm,
        country_id: countryId || country_id,
        post_code: postCode || post_code,
      },
    };

    reservations.forEach((reservation) => {
      switch (reservation.resource_class_name) {
        case 'Ship':
        case 'ScheduledTripResource':
          rebuildData.reservations.push({
            resource_class_name: reservation.resource_class_name,
            route_id: reservation.route_id,
            forward: {
              trip_id: reservation.trip_id,
              first_travel_date: reservation.first_travel_date._value,
              from_route_stop_id: reservation.from_route_stop_attributes.id,
              to_route_stop_id: reservation.to_route_stop_attributes.id,
              passenger_types: getPassengerTypes(
                reservation.passenger_ids,
                passengerTypes
              ),
              vehicle_types: reservation.vehicle_ids.map((vehicleId) =>
                getVehicleType(vehicleId, vehiclesTypes)
              ),
            },
            segments: ['forward'],
          });
          break;
        case 'Package':
          rebuildData.reservations.push({
            resource_class_name: reservation.resource_class_name,
            resource_id: reservation.resource_id,
            first_travel_date: reservation.first_travel_date._value,
            passenger_types: getPassengerTypes(
              reservation.passenger_ids,
              passengerTypes
            ),
            sub_reservations:
              reservation.sub_reservations_attributes?.map(
                (subReservation) => ({
                  from_route_stop_id:
                    subReservation.from_route_stop_attributes?.id || null,
                  to_route_stop_id:
                    subReservation.to_route_stop_attributes?.id || null,
                  bed_configuration: subReservation.bed_configuration || null,
                })
              ) || [],
          });
          break;
        case 'Accommodation':
          rebuildData.reservations.push({
            resource_class_name: reservation.resource_class_name,
            resource_id: reservation.resource_id,
            first_travel_date: reservation.first_travel_date._value,
            last_travel_date: reservation.last_travel_date._value,
            passenger_types: getPassengerTypes(
              reservation.passenger_ids,
              passengerTypes
            ),
            bed_configuration: reservation.bed_configuration,
          });
          break;
        case 'Item':
          rebuildData.reservations.push({
            resource_class_name: reservation.resource_class_name,
            resource_id: reservation.resource_id,
            first_travel_date: reservation.first_travel_date._value,
            last_travel_date: reservation.last_travel_date._value,
            quantity: reservation.quantity,
          });
          break;
        case 'GenericResource':
          rebuildData.reservations.push({
            resource_class_name: reservation.resource_class_name,
            resource_id: reservation.resource_id,
            first_travel_date: reservation.first_travel_date._value,
            passenger_types: getPassengerTypes(
              reservation.passenger_ids,
              passengerTypes
            ),
          });
          break;
        default:
          break;
      }
    });

    rebuildData.customer = result.customer;
    const url = IS_ONE_SEALINK
      ? `${ecom.craftConfig.siteUrl}/bookings`
      : `${window.location.origin}/cart`;
    result.rebuild_url = `${url}/rebuild?json=${encodeURIComponent(
      JSON.stringify(rebuildData)
    )}`;
  }
  return result;
};

let cartModal = null;
const mouseEvent = (e) => {
  // On-exit modal only shows up when user move the cursor out of window from top.
  const shouldShowExitIntent =
    !e.toElement && !e.relatedTarget && e.clientY < 10;

  if (
    shouldShowExitIntent &&
    hasUnpaidBooking() &&
    !inStorage(getBooking().id)
  ) {
    const buttonText = onOverviewOrCheckout()
      ? 'Continue with booking'
      : 'Return to booking';
    const redirectBtn = document.querySelector(
      '.abandon-cart-modal--return-btn'
    );
    redirectBtn.innerText = buttonText;

    cartModal.show();
  }
};

export function initializeAbandonCartEvent() {
  removeOutDated();

  document.addEventListener('DOMContentLoaded', () => {
    const modalElement = document.getElementById('abandon-cart-modal');
    cartModal = new window.BsModal(modalElement);
    const form = document.querySelector('#abandon-cart-modal form');

    form?.addEventListener('submit', (e) => {
      e.preventDefault();
      const formData = Object.fromEntries(new FormData(e.target));
      const { emailAddress } = formData;
      const emailMeBtn = document.querySelector(
        '.abandon-cart-modal--email-me'
      );
      if (emailAddress) {
        const payload = buildPayload(emailAddress);
        fireEvent(EVENT_NAME, payload);
        emailMeBtn.className = emailMeBtn.className.replace(
          'btn-outline-secondary',
          'btn-outline-success disabled'
        );
        emailMeBtn.innerHTML = 'Success';
      }
    });

    document.addEventListener('mouseout', mouseEvent);

    modalElement.addEventListener('hidden.bs.modal', () => {
      storeModalState(getBooking().id);

      if (onOverviewOrCheckout()) {
        // Do nothing if user is on overview or checkout page
        return;
      }

      if (window.ecom.REACT_HISTORY) {
        window.ecom.REACT_HISTORY.push(IS_ONE_SEALINK ? '/bookings' : '/cart');
      } else {
        window.location.href = IS_ONE_SEALINK
          ? `${ecom.craftConfig.api_prefix}/bookings`
          : '/cart';
      }
    });
  });
}
